import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faKey,
  faEye,
  faEyeSlash,
  faArrowLeft,
  faLock,
  faCheckCircle,
  faExclamationCircle,
  faShield,
} from "@fortawesome/free-solid-svg-icons";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toastError, toastSuccess } from "../utils/toast";
import { useAuth } from "../hooks";

const OTP_LENGTH = 6;

const OtpInput = ({
  value,
  onChange,
  length = OTP_LENGTH,
  autoFocus = false,
  disabled = false,
}) => {
  const inputsRef = useRef([]);

  const focusIndex = (i) => {
    if (i < 0 || i >= length) return;
    inputsRef.current[i]?.focus();
    inputsRef.current[i]?.select();
  };

  const handleChange = (e, idx) => {
    const raw = e.target.value.replace(/\D/g, "");

    if (!raw) {
      const next = [...value];
      next[idx] = "";
      onChange(next);
      return;
    }

    const next = [...value];
    const chars = raw.split("");
    let cursor = idx;
    for (const ch of chars) {
      if (cursor >= length) break;
      next[cursor] = ch;
      cursor++;
    }
    onChange(next);
    focusIndex(Math.min(cursor, length - 1));
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...value];
      if (next[idx]) {
        next[idx] = "";
        onChange(next);
      } else if (idx > 0) {
        next[idx - 1] = "";
        onChange(next);
        focusIndex(idx - 1);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusIndex(idx - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusIndex(idx + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData?.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, length);
    if (!text) return;
    const next = Array(length).fill("");
    text.split("").forEach((ch, i) => (next[i] = ch));
    onChange(next);
    focusIndex(Math.min(text.length, length - 1));
  };

  return (
    <div className="flex items-center gap-2 w-full">
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => (inputsRef.current[idx] = el)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={value[idx] || ""}
          autoFocus={autoFocus && idx === 0}
          disabled={disabled}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className="flex-1 min-w-0 h-12 text-center text-lg font-semibold rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-blue-700 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      ))}
    </div>
  );
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const { forgotPassword, verifyOtp, resetPassword } = useAuth();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [login, setLogin] = useState("");
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
    requirements: {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false,
    },
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");

  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpError, setOtpError] = useState("");

  const otpComplete = otp.every((d) => d !== "");
  const otpString = otp.join("");

  useEffect(() => {
    document.title = "Reset Password - Shop Kiganjani";
  }, []);

  useEffect(() => {
    if (!otpComplete) {
      setOtpVerified(false);
      setOtpError("");
      return;
    }

    let cancelled = false;
    setOtpVerifying(true);
    setOtpError("");

    const run = async () => {
      try {
        await verifyOtp({ login: login.trim(), otp: otpString });
        if (!cancelled) setOtpVerified(true);
      } catch (err) {
        if (!cancelled) {
          setOtpVerified(false);
          setOtpError(err?.message || "Invalid reset code.");
        }
      } finally {
        if (!cancelled) setOtpVerifying(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpComplete, otpString]);

  const resetFeedback = () => {
    setServerError("");
    setErrors({});
  };

  const validatePasswordStrength = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const metCount = Object.values(requirements).filter(Boolean).length;

    let score = 0;
    let label = "";
    let color = "";

    if (password.length === 0) {
      score = 0;
      label = "";
      color = "";
    } else if (metCount <= 2) {
      score = 1;
      label = "Weak";
      color = "text-red-500";
    } else if (metCount === 3) {
      score = 2;
      label = "Medium";
      color = "text-yellow-500";
    } else if (metCount >= 4) {
      score = 3;
      label = "Strong";
      color = "text-green-500";
    }

    setPasswordStrength({ score, label, color, requirements });
  };

  const getStrengthBarColor = () => {
    if (passwordStrength.score === 0) return "bg-gray-200";
    if (passwordStrength.score === 1) return "bg-red-500";
    if (passwordStrength.score === 2) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthBarWidth = () => {
    if (passwordStrength.score === 0) return "0%";
    if (passwordStrength.score === 1) return "33%";
    if (passwordStrength.score === 2) return "66%";
    return "100%";
  };

  async function handleRequestOtp(e) {
    e.preventDefault();
    resetFeedback();

    const newErrors = {};
    if (!login.trim()) newErrors.login = "Email or username is required";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSubmitting(true);
    try {
      await forgotPassword({ login: login.trim() });
      toastSuccess("Reset code sent. Check your email.");
      setStep(2);
    } catch (err) {
      const msg = err?.message || "Could not send reset code.";
      setServerError(msg);
      toastError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    resetFeedback();

    if (!otpVerified) {
      setErrors({ otp: "Please verify the reset code first." });
      return;
    }

    const newErrors = {};

    if (!newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    } else if (passwordStrength.score < 3) {
      newErrors.newPassword = "Please use a stronger password";
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSubmitting(true);
    try {
      await resetPassword({
        login: login.trim(),
        otp: otpString,
        new_password: newPassword.trim(),
      });
      toastSuccess("Password reset successfully. Please log in.");
      setTimeout(() => navigate("/login"), 400);
    } catch (err) {
      const msg = err?.message || "Could not reset password.";
      setServerError(msg);
      toastError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const resetAll = () => {
    setStep(1);
    setOtp(Array(OTP_LENGTH).fill(""));
    setNewPassword("");
    setConfirmPassword("");
    setPasswordStrength({
      score: 0,
      label: "",
      color: "",
      requirements: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
      },
    });
    setOtpVerified(false);
    setOtpError("");
    setOtpVerifying(false);
    resetFeedback();
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
        {/* Header */}
        <div className="flex flex-col items-center gap-2 mb-8">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-700">
            <FontAwesomeIcon icon={faKey} className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {step === 1 ? "Forgot Password" : "Reset Password"}
          </h1>
          <p className="text-sm text-gray-600 text-center">
            {step === 1
              ? "Enter your email or username to receive a reset code"
              : otpVerified
                ? "Reset code verified. Now set your new password"
                : "Enter the 6-digit reset code to continue"}
          </p>
        </div>

        {serverError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-600">{serverError}</p>
          </div>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <form onSubmit={handleRequestOtp} className="flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor="login"
                className="text-gray-800 font-medium text-base"
              >
                Email or Username
              </Label>
              <Input
                id="login"
                type="text"
                placeholder="Enter your email or username"
                value={login}
                onChange={(e) => {
                  setLogin(e.target.value);
                  resetFeedback();
                }}
                className={
                  errors.login ? "border-red-500 ring-1 ring-red-500" : ""
                }
              />
              {errors.login && (
                <span className="text-xs text-red-500">{errors.login}</span>
              )}
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-700 hover:bg-blue-800 text-white disabled:bg-gray-400 disabled:cursor-not-allowed h-11"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                "Send Reset Code"
              )}
            </Button>

            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-blue-800 transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" />
                Back to login
              </Link>
            </div>
          </form>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <form onSubmit={handleResetPassword} className="flex flex-col gap-5">
            {/* OTP */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-gray-800 font-medium text-base">
                Reset code
              </Label>

              <OtpInput
                value={otp}
                onChange={(next) => {
                  setOtp(next);
                  resetFeedback();
                }}
                length={OTP_LENGTH}
                autoFocus
                disabled={otpVerifying || otpVerified}
              />

              {otpVerifying && (
                <span className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                  <span className="h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  Verifying code...
                </span>
              )}

              {otpVerified && (
                <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                  <FontAwesomeIcon icon={faCheckCircle} className="h-3 w-3" />
                  Code verified. You can now set a new password.
                </span>
              )}

              {otpError && !otpVerifying && (
                <span className="text-xs text-red-500 flex items-center gap-1 mt-1">
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    className="h-3 w-3"
                  />
                  {otpError}
                </span>
              )}

              {errors.otp && (
                <span className="text-xs text-red-500">{errors.otp}</span>
              )}
            </div>

            {/* Password fields — only after OTP is verified */}
            {otpVerified && (
              <>
                {/* New password */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <FontAwesomeIcon
                      icon={faLock}
                      className="text-blue-600 h-3.5 w-3.5"
                    />
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      name="newPassword"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        validatePasswordStrength(e.target.value);
                        resetFeedback();
                      }}
                      placeholder="Enter new password (min 8 characters)"
                      className={`pr-10 ${
                        errors.newPassword
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300"
                      }`}
                      disabled={submitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-600 transition-colors"
                    >
                      <FontAwesomeIcon
                        icon={showNewPassword ? faEyeSlash : faEye}
                        className="h-4 w-4"
                      />
                    </button>
                  </div>

                  {errors.newPassword && (
                    <p className="text-red-500 text-xs flex items-center gap-1 mt-1">
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3"
                      />
                      {errors.newPassword}
                    </p>
                  )}

                  {newPassword.length > 0 && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2.5 max-w-xs">
                            <div
                              className={`h-2.5 rounded-full transition-all duration-300 ${getStrengthBarColor()}`}
                              style={{ width: getStrengthBarWidth() }}
                            />
                          </div>
                          <span
                            className={`text-xs font-medium ${passwordStrength.color} min-w-[50px]`}
                          >
                            {passwordStrength.label}
                          </span>
                        </div>
                        <FontAwesomeIcon
                          icon={faShield}
                          className={`h-4 w-4 ${
                            passwordStrength.label === "Strong"
                              ? "text-green-500"
                              : passwordStrength.label === "Medium"
                                ? "text-yellow-500"
                                : passwordStrength.label === "Weak"
                                  ? "text-red-500"
                                  : "text-gray-300"
                          }`}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-2 border-t border-gray-200">
                        <div
                          className={`flex items-center gap-1.5 text-xs ${
                            passwordStrength.requirements.length
                              ? "text-green-600"
                              : "text-gray-600"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              passwordStrength.requirements.length
                                ? faCheckCircle
                                : faExclamationCircle
                            }
                            className={`h-3 w-3 ${
                              passwordStrength.requirements.length
                                ? "text-green-500"
                                : "text-gray-300"
                            }`}
                          />
                          <span>At least 8 characters</span>
                        </div>

                        <div
                          className={`flex items-center gap-1.5 text-xs ${
                            passwordStrength.requirements.uppercase
                              ? "text-green-600"
                              : "text-gray-600"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              passwordStrength.requirements.uppercase
                                ? faCheckCircle
                                : faExclamationCircle
                            }
                            className={`h-3 w-3 ${
                              passwordStrength.requirements.uppercase
                                ? "text-green-500"
                                : "text-gray-300"
                            }`}
                          />
                          <span>Uppercase letter (A-Z)</span>
                        </div>

                        <div
                          className={`flex items-center gap-1.5 text-xs ${
                            passwordStrength.requirements.lowercase
                              ? "text-green-600"
                              : "text-gray-600"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              passwordStrength.requirements.lowercase
                                ? faCheckCircle
                                : faExclamationCircle
                            }
                            className={`h-3 w-3 ${
                              passwordStrength.requirements.lowercase
                                ? "text-green-500"
                                : "text-gray-300"
                            }`}
                          />
                          <span>Lowercase letter (a-z)</span>
                        </div>

                        <div
                          className={`flex items-center gap-1.5 text-xs ${
                            passwordStrength.requirements.number
                              ? "text-green-600"
                              : "text-gray-600"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              passwordStrength.requirements.number
                                ? faCheckCircle
                                : faExclamationCircle
                            }
                            className={`h-3 w-3 ${
                              passwordStrength.requirements.number
                                ? "text-green-500"
                                : "text-gray-300"
                            }`}
                          />
                          <span>Number (0-9)</span>
                        </div>

                        <div
                          className={`flex items-center gap-1.5 text-xs col-span-1 sm:col-span-2 ${
                            passwordStrength.requirements.special
                              ? "text-green-600"
                              : "text-gray-600"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              passwordStrength.requirements.special
                                ? faCheckCircle
                                : faExclamationCircle
                            }
                            className={`h-3 w-3 ${
                              passwordStrength.requirements.special
                                ? "text-green-500"
                                : "text-gray-300"
                            }`}
                          />
                          <span>
                            Special character (!@#$%^&*(),.?":{}|&lt;&gt;)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm password */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-blue-600 h-3.5 w-3.5"
                    />
                    Confirm New Password
                  </Label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        resetFeedback();
                      }}
                      placeholder="Confirm new password"
                      className={`pr-10 ${
                        errors.confirmPassword
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300"
                      }`}
                      disabled={submitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-600 transition-colors"
                    >
                      <FontAwesomeIcon
                        icon={showConfirmPassword ? faEyeSlash : faEye}
                        className="h-4 w-4"
                      />
                    </button>
                  </div>

                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs flex items-center gap-1 mt-1">
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="h-3 w-3"
                      />
                      {errors.confirmPassword}
                    </p>
                  )}

                  {confirmPassword.length > 0 && newPassword.length > 0 && (
                    <div
                      className={`flex items-center gap-2 p-2.5 rounded-lg ${
                        newPassword === confirmPassword
                          ? "bg-green-50 border border-green-200"
                          : "bg-red-50 border border-red-200"
                      }`}
                    >
                      {newPassword === confirmPassword ? (
                        <>
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="text-green-500 h-4 w-4"
                          />
                          <span className="text-green-700 text-xs font-medium">
                            Passwords match
                          </span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon
                            icon={faExclamationCircle}
                            className="text-red-500 h-4 w-4"
                          />
                          <span className="text-red-700 text-xs font-medium">
                            Passwords do not match
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white disabled:bg-gray-400 disabled:cursor-not-allowed h-11"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Resetting...
                    </span>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </>
            )}

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={resetAll}
                className="inline-flex items-center gap-1 font-medium text-gray-600 hover:text-blue-800 transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" />
                Back
              </button>
              <Link
                to="/login"
                className="text-gray-600 hover:text-blue-800 transition-colors font-medium"
              >
                Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </main>
  );
};

export default ResetPassword;